import { Eye, Landmark, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { bankAccountMethodLabel, type EmployeeBankAccount } from '../bankAccounts';

/** A friendly, partly-masked one-liner for the account's main identifier. */
function maskedIdentifier(a: EmployeeBankAccount): string {
  if (a.method === 'wise' || a.method === 'payoneer') return a.email || '—';
  const raw = a.method === 'local' ? a.account_number : a.iban || a.account_number;
  if (!raw) return a.email || '—';
  const last4 = raw.replace(/\s+/g, '').slice(-4);
  return `•••• ${last4}`;
}

/** Full (unmasked) field rows for the details view — only what's filled in. */
function detailRows(a: EmployeeBankAccount): { label: string; value: string; mono?: boolean }[] {
  const rows: { label: string; value: string; mono?: boolean }[] = [
    { label: 'Method', value: bankAccountMethodLabel(a.method) },
    { label: 'Account holder', value: a.account_holder },
    { label: 'Bank', value: a.bank_name },
    { label: 'Bank address', value: a.bank_address },
    { label: 'IBAN', value: a.iban, mono: true },
    { label: 'SWIFT / BIC', value: a.swift_bic, mono: true },
    { label: 'Account number', value: a.account_number, mono: true },
    { label: 'Routing / sort code', value: a.routing_number, mono: true },
    { label: 'Account email', value: a.email },
    { label: 'Currency', value: a.currency },
    { label: 'Country', value: a.country },
    { label: 'Notes', value: a.notes },
  ];
  return rows.filter((r) => r.value);
}

interface Props {
  accounts: EmployeeBankAccount[];
  isPending: boolean;
  /** When false the cards are read-only (no add/edit/delete) — view stays available. */
  canManage: boolean;
  /** Navigate to the add page (only used when canManage). */
  onAdd?: () => void;
  /** Navigate to the edit page for this account (only used when canManage). */
  onEdit?: (account: EmployeeBankAccount) => void;
  onDelete?: (id: string | number) => Promise<unknown> | void;
  /** Empty-state copy; differs between "my" cabinet view and the HR view. */
  emptyText?: string;
}

/** Compact card list of payout accounts. A view icon opens read-only details;
 * Add/Edit route to a dedicated form page (provided by the parent), delete is
 * inline — both only when `canManage`. */
export function BankAccountsManager({
  accounts,
  isPending,
  canManage,
  onAdd,
  onEdit,
  onDelete,
  emptyText = 'No bank accounts yet.',
}: Props) {
  const [viewing, setViewing] = useState<EmployeeBankAccount | null>(null);

  if (isPending) return <Skeleton className="h-24 max-w-md" />;

  return (
    <div className="max-w-md space-y-3">
      {accounts.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground flex flex-col items-center gap-2 py-8 text-sm">
            <Landmark className="size-6 opacity-60" />
            {emptyText}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {accounts.map((a) => (
            <Card key={a.public_id} className="overflow-hidden">
              <CardContent className="flex items-center gap-3 p-3">
                <span className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-md">
                  <Landmark className="size-4" />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium">
                      {a.label || bankAccountMethodLabel(a.method)}
                    </span>
                    {a.is_default ? (
                      <Star className="size-3.5 shrink-0 fill-amber-400 text-amber-400" aria-label="Default" />
                    ) : null}
                  </div>
                  <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                    <span className="font-mono">{maskedIdentifier(a)}</span>
                    <span aria-hidden>·</span>
                    <span>{bankAccountMethodLabel(a.method)}</span>
                    {a.currency ? (
                      <>
                        <span aria-hidden>·</span>
                        <span>{a.currency}</span>
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="flex shrink-0 items-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    aria-label="View bank account details"
                    onClick={() => setViewing(a)}
                  >
                    <Eye className="size-4" />
                  </Button>
                  {canManage ? (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label="Edit bank account"
                        onClick={() => onEdit?.(a)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive size-8"
                            aria-label="Remove bank account"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove this bank account?</AlertDialogTitle>
                            <AlertDialogDescription>This can’t be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => onDelete?.(a.public_id)}>
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {canManage && onAdd ? (
        <Button variant="outline" size="sm" onClick={onAdd}>
          <Plus /> Add bank account
        </Button>
      ) : null}

      <Dialog open={Boolean(viewing)} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewing?.label || (viewing ? bankAccountMethodLabel(viewing.method) : 'Bank account')}
              {viewing?.is_default ? (
                <Badge variant="secondary" className="gap-1">
                  <Star className="size-3 fill-amber-400 text-amber-400" /> Default
                </Badge>
              ) : null}
            </DialogTitle>
          </DialogHeader>
          {viewing ? (
            <dl className="divide-y text-sm">
              {detailRows(viewing).map((r) => (
                <div key={r.label} className="grid grid-cols-3 gap-2 py-2">
                  <dt className="text-muted-foreground">{r.label}</dt>
                  <dd className={`col-span-2 break-all ${r.mono ? 'font-mono' : ''}`}>{r.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
